import { useOrgClock } from "./lib/orgClock";
import { usePresenceClient } from "./lib/presence";
import * as Tooltip from "@radix-ui/react-tooltip";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router";
import { Toaster } from "sonner";
import { ApiError } from "./lib/api";
import { useMeQuery } from "./lib/queries";
import { useLiveEvents } from "./lib/live";
import { useIsDark } from "./lib/prefs";
import { browserSession, rememberOAuthReturn, takeOAuthReturn } from "./lib/oauthReturn";
import type { Me } from "./lib/types";
import { PendingPage, SignInPage, WelcomePage } from "./pages/AuthPages";
import { MfaPage } from "./pages/MfaPage";
import { hasCap, hasPipeline, hasProspecting } from "./lib/roles";
import { PipelinePage } from "./pages/PipelinePage";
import { TopNav } from "./components/AppShell";
import { ActingBanner } from "./components/ActingRole";
import { GuidedModuleBanner } from "./pages/TrainingManualPage";
import { LoanDrawer } from "./components/LoanDrawer";
import { CommandPalette } from "./components/CommandPalette";
import { Button, Spinner } from "./components/ui";
import { FeedbackLayer } from "./components/feedback/FeedbackLayer";

// Dashboard, goals and admin are lazy-loaded to keep the grid route's initial JS small.
const TeamPage = lazy(() => import("./pages/TeamPage").then((m) => ({ default: m.TeamPage })));
const GoalsPage = lazy(() => import("./pages/GoalsPage").then((m) => ({ default: m.GoalsPage })));
const AdminPage = lazy(() => import("./pages/AdminPage").then((m) => ({ default: m.AdminPage })));
const PeoplePage = lazy(() => import("./pages/PeoplePage").then((m) => ({ default: m.PeoplePage })));
const SecurityPage = lazy(() => import("./pages/SecurityPage").then((m) => ({ default: m.SecurityPage })));
const ProspectingPage = lazy(() => import("./pages/ProspectingPage").then((m) => ({ default: m.ProspectingPage })));
const SigningPage = lazy(() => import("./pages/SigningPage").then((m) => ({ default: m.SigningPage })));
const BrokerLinksPage = lazy(() => import("./pages/BrokerLinksPage").then((m) => ({ default: m.BrokerLinksPage })));
const AccountPage = lazy(() => import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage").then((m) => ({ default: m.DocumentsPage })));
const LosWorkbenchPage = lazy(() => import("./pages/LosWorkbenchPage").then((m) => ({ default: m.LosWorkbenchPage })));
const ClosingReconciliationPage = lazy(() => import("./pages/ClosingReconciliationPage").then((m) => ({ default: m.ClosingReconciliationPage })));
const CoreLoanWorkspacePage = lazy(() => import("./pages/CoreLoanWorkspacePage").then((m) => ({ default: m.CoreLoanWorkspacePage })));
const CreditOcrPage = lazy(() => import("./pages/CreditOcrPage").then((m) => ({ default: m.CreditOcrPage })));
const SeqPage = lazy(() => import("./pages/SeqPage").then((m) => ({ default: m.SeqPage })));
const ConditionsPage = lazy(() => import("./pages/ConditionsPage").then((m) => ({ default: m.ConditionsPage })));
const RoleHomePage = lazy(() => import("./pages/RoleHomePage").then((m) => ({ default: m.RoleHomePage })));
const SmartDropPortalPage = lazy(() => import("./pages/SmartDropPortalPage").then((m) => ({ default: m.SmartDropPortalPage })));
const DocumentTriagePage = lazy(() => import("./pages/DocumentTriagePage").then((m) => ({ default: m.DocumentTriagePage })));
const TrainingManualPage = lazy(() => import("./pages/TrainingManualPage").then((m) => ({ default: m.TrainingManualPage })));
const UrlaApplicationPage = lazy(() => import("./pages/UrlaApplicationPage").then((m) => ({ default: m.UrlaApplicationPage })));
const ScenariosPage = lazy(() => import("./pages/ScenariosPage").then((m) => ({ default: m.ScenariosPage })));
// Preview screens (src/lib/preview.ts): the page itself checks Preview and the API refuses without it.
const FeedbackPage = lazy(() => import("./pages/FeedbackPage").then((m) => ({ default: m.FeedbackPage })));
const ManualPage = lazy(() => import("./pages/ManualPage").then((m) => ({ default: m.ManualPage })));

export function App() {
  const dark = useIsDark();
  return (
    <BrowserRouter>
      <Tooltip.Provider delayDuration={200}>
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center gap-2 text-ink-2">
              <Spinner /> Loading…
            </div>
          }
        >
          <Routes>
            <Route path="/drop/:token" element={<SmartDropPortalPage />} />
            <Route path="/portal/drop/:token" element={<SmartDropPortalPage />} />
            <Route path="/pos/:token" element={<SmartDropPortalPage initialMode="pos" />} />
            <Route path="/sign/:token" element={<SmartDropPortalPage initialMode="sign" />} />
            <Route path="*" element={<AuthGate />} />
          </Routes>
        </Suspense>
        <Toaster position="bottom-left" theme={dark ? "dark" : "light"} closeButton richColors={false} toastOptions={{ className: "font-sans" }} />
      </Tooltip.Provider>
    </BrowserRouter>
  );
}

function AuthGate() {
  const me = useMeQuery();
  // LoanShip MCP: after sign-in (and the passkey or Duo step), go back to the AI app's consent page.
  useState(() => rememberOAuthReturn(window.location.search, browserSession()));
  const active = me.data?.user.status === "active";
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!active) return;
    const back = takeOAuthReturn(browserSession());
    if (back) {
      setLeaving(true);
      window.location.replace(back);
    }
  }, [active]);
  if (leaving) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-ink-2">
        <Spinner /> Returning to connect your AI app…
      </div>
    );
  }
  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-ink-2">
        <Spinner /> Loading…
      </div>
    );
  }
  if (me.error) {
    const e = me.error;
    if (e instanceof ApiError && e.status === 401) return <SignInPage />;
    // Signed in with Microsoft, not yet confirmed with a passkey: the only page the session can use.
    if (e instanceof ApiError && e.code === "mfa_required") return <MfaPage />;
    if (e instanceof ApiError && (e.code === "pending_approval" || e.code === "disabled")) return <PendingPage kind={e.code} />;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-ink-2">
        <div className="font-display text-[18px] font-semibold text-ink">Couldn't reach Pipeline</div>
        <div>{e.message}</div>
        <Button variant="primary" onClick={() => void me.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!me.data) return null;
  if (me.data.user.status === "pending") return <PendingPage kind="pending_approval" email={me.data.user.email} />;
  if (me.data.user.status === "disabled") return <PendingPage kind="disabled" email={me.data.user.email} />;
  // Signed-in LendSure members without a role get a welcome screen, not an error (and no live feed).
  if (!hasPipeline(me.data) && !hasProspecting(me.data)) return <WelcomePage name={me.data.user.display_name} email={me.data.user.email} />;
  return <Shell me={me.data} />;
}

function landing(me: Me) {
  // A session acting in a seat lands on that seat's home.
  if (me.user.acting) return "/role";
  // Prospecting-only roles (marketing, compliance reviewer) land in Prospecting.
  if (!hasPipeline(me)) return "/prospecting";
  // Team leads, division managers and executives land on the dashboard; producers and Superadmin on the grid.
  const dashboardFirst = hasCap(me, "see_team_comp") && !hasCap(me, "manage_all_teams");
  return dashboardFirst ? "/team" : "/pipeline";
}

function Shell({ me }: { me: Me }) {
  usePresenceClient(me.user.id);
  useOrgClock(me.settings.timezone);
  const live = useLiveEvents(me);
  const [params, setParams] = useSearchParams();
  const [palette, setPalette] = useState(false);
  const loanId = params.get("loan");

  const openLoan = useCallback(
    (id: string) =>
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.set("loan", id);
        return n;
      }),
    [setParams],
  );
  const closeLoan = useCallback(
    () =>
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.delete("loan");
        return n;
      }),
    [setParams],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <TopNav me={me} live={live} onPalette={() => setPalette(true)} />
      <ActingBanner me={me} />
      <GuidedModuleBanner />
      <main id="main" className="min-h-0 flex-1 overflow-auto">
        <Suspense
          fallback={
            <div className="flex items-center gap-2 p-6 text-ink-2">
              <Spinner /> Loading…
            </div>
          }
        >
        <Routes>
          <Route path="/" element={<Navigate to={landing(me)} replace />} />
          <Route path="/conditions" element={<ConditionsPage />} />
          <Route path="/conditions/loans/:loanId" element={<ConditionsPage />} />
          <Route path="/conditions/review/:conditionId" element={<ConditionsPage />} />
          <Route path="/role" element={<RoleHomePage />} />
          <Route path="/pipeline" element={<PipelinePage onOpenLoan={openLoan} />} />
          <Route path="/los" element={<LosWorkbenchPage me={me} />} />
          <Route path="/team" element={<TeamPage onOpenLoan={openLoan} />} />
          <Route path="/goals" element={<GoalsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/prospecting/*" element={<ProspectingPage />} />
          <Route path="/scenarios" element={<ScenariosPage me={me} onOpenLoan={openLoan} />} />
          <Route path="/broker-links" element={<BrokerLinksPage onOpenLoan={openLoan} />} />
          <Route path="/signatures" element={<SigningPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:loanId" element={<DocumentsPage />} />
          <Route path="/core-workspace" element={<CoreLoanWorkspacePage />} />
          <Route path="/loans/:loanId/workspace" element={<CoreLoanWorkspacePage />} />
          <Route path="/loans/:loanId/seq" element={<SeqPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          {["/closing-reconciliation", "/loans/:loanId/closing-reconciliation"].map(path => <Route key={path} path={path} element={<ClosingReconciliationPage />} />)}
          <Route path="/loans/:loanId/credit-ocr" element={<CreditOcrPage />} />
          <Route path="/smart-drops" element={<DocumentTriagePage liveMode />} />
          <Route path="/training" element={<TrainingManualPage />} />
          <Route path="/urla" element={<UrlaApplicationPage />} />
          <Route path="/manual" element={<ManualPage />} />
          <Route path="*" element={<Navigate to={landing(me)} replace />} />
        </Routes>
        </Suspense>
      </main>
      {loanId ? <LoanDrawer key={loanId} loanId={loanId} onClose={closeLoan} /> : null}
      <CommandPalette me={me} open={palette} onOpenChange={setPalette} onOpenLoan={openLoan} />
      <FeedbackLayer />
    </div>
  );
}
